import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiShare2, FiArrowRight, FiZap, FiLock } = FiIcons;

export default function Hero({ onCreateRoom, onJoinRoom }) {
  return (
    <section className="relative pt-20 pb-16 md:pt-32 md:pb-24 overflow-hidden">
      {/* Background Orbs */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full -z-10 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-400/10 blur-[120px] rounded-full" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-indigo-400/10 blur-[120px] rounded-full" />
      </div>

      <div className="max-w-6xl mx-auto px-4 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100 mb-8">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
            </span>
            <span className="text-sm font-bold text-blue-700 uppercase tracking-wider">Real-time Sync Active</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-black text-gray-900 tracking-tight mb-8 leading-[1.1]">
            Share Anything Between <br />
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">
              Any Device Instantly
            </span>
          </h1>

          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-12 leading-relaxed font-medium">
            The simplest way to move text and files across your ecosystem. No accounts, 
            no logins, just instant end-to-end encrypted sharing.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <motion.button
              onClick={onCreateRoom}
              whileHover={{ scale: 1.05, boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)' }}
              whileTap={{ scale: 0.95 }}
              className="group w-full sm:w-auto px-10 py-5 bg-blue-600 text-white rounded-2xl font-black text-lg flex items-center justify-center gap-3 shadow-xl shadow-blue-200 transition-all hover:bg-blue-700"
            >
              <SafeIcon icon={FiZap} className="w-6 h-6" />
              Start Sharing
              <SafeIcon icon={FiArrowRight} className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </motion.button>

            <motion.button
              onClick={onJoinRoom}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="w-full sm:w-auto px-10 py-5 bg-white text-gray-900 border-2 border-gray-100 rounded-2xl font-black text-lg flex items-center justify-center gap-3 transition-all hover:border-blue-200 hover:bg-gray-50"
            >
              <SafeIcon icon={FiShare2} className="w-6 h-6 text-blue-600" />
              Join Room
            </motion.button>
          </div>

          <div className="mt-16 flex flex-wrap justify-center gap-8 md:gap-16">
            <TrustBadge icon={FiLock} text="Password Protected" />
            <TrustBadge icon={FiZap} text="Zero Latency" />
            <TrustBadge icon={FiShare2} text="Cross-Platform" />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function TrustBadge({ icon, text }) {
  return (
    <div className="flex items-center gap-2 text-gray-400 font-bold uppercase tracking-widest text-xs">
      <SafeIcon icon={icon} className="w-4 h-4" />
      <span>{text}</span>
    </div>
  );
}